/**
 * Reply composer + take-over bar (3rd column footer). Ported from the storybook
 * `final/_helpdesk.tsx` `Composer` (L825-1027).
 *
 * Two modes, switched by a Public reply ↔ Internal note toggle:
 *   • reply — a customer-facing message; needs `human_handling` (the human must
 *     be driving) and routes to `onSend`. Unchanged from before.
 *   • note — an operator-private annotation the customer/AI never see; enabled
 *     in ANY status (an operator can note an ai_handling / queued / resolved
 *     conversation), warning-tinted, and routes to `onSendNote`. The toggle's
 *     "Internal note" option only appears when `canNote` is true.
 *
 * The header bar mirrors the driver — "AI is replying" with a Take over button
 * (`ai_handling`), "You're replying" with Hand back / Resolve (`human_handling`),
 * or a Claim button (`queued`). Those go through a confirm dialog.
 */
import * as React from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Textarea,
  cn,
} from "@cometchat-ai/ui-dashboard";
import { useTranslation } from "react-i18next";

import {
  ArrowUp,
  Check,
  ChevronDown,
  Headset,
  Note,
  SendHorizontal,
  Sparkles,
} from "../components/icons";
import type { SessionStatus } from "../handoff/api";
import { useConfirm } from "./use-confirm";

export function Composer({
  status,
  custName,
  sending,
  sendingNote,
  onSend,
  onSendNote,
  onTakeover,
  onHandback,
  onResolve,
  onClaim,
  canTakeover,
  canHandback,
  canResolve,
  canClaim,
  canNote,
  isOwnedByMe,
  canReplyAll,
  ownerName,
}: {
  status: SessionStatus;
  custName: string;
  sending: boolean;
  sendingNote: boolean;
  onSend: (text: string) => void;
  onSendNote: (text: string) => void;
  onTakeover: () => void;
  onHandback: () => void;
  onResolve: () => void;
  onClaim: () => void;
  canTakeover: boolean;
  canHandback: boolean;
  canResolve: boolean;
  canClaim: boolean;
  canNote: boolean;
  /** THIS conversation is claimed by the current user — gates the owner-only
   *  public reply. Hand back / Resolve are already gated at the parent. */
  isOwnedByMe: boolean;
  /** `handoff:reply-all` — may reply even to a chat owned by a teammate. */
  canReplyAll: boolean;
  /** Who currently owns it, for the "Handled by {name}" label when not mine. */
  ownerName: string | null;
}) {
  const { t } = useTranslation("workspace");
  const [text, setText] = React.useState("");
  const [mode, setMode] = React.useState<"reply" | "note">("reply");
  const { confirm, confirmDialog } = useConfirm();

  const isHuman = status === "human_handling";
  const isAi = status === "ai_handling";
  const isQueued = status === "queued";
  // May reply here: I own it, OR I hold reply-all (jump into a teammate's chat).
  const canReplyHere = isOwnedByMe || canReplyAll;
  // A teammate owns this chat (drives the "Handled by {name}" header, shown
  // even to reply-all holders so ownership stays visible).
  const otherOwner = isHuman && !isOwnedByMe;
  // …and I can't reply to it — read-only + internal note only.
  const readOnlyHere = isHuman && !canReplyHere;

  const isNote = mode === "note";
  // Public reply needs a human driver AND reply rights; a note can be added in
  // any status by any operator with the note permission (team collaboration).
  const canType = isNote ? canNote : isHuman && canReplyHere;
  const busy = isNote ? sendingNote : sending;

  // If the note permission disappears while in note mode, fall back to reply.
  React.useEffect(() => {
    if (isNote && !canNote) setMode("reply");
  }, [isNote, canNote]);

  // Someone else owns the chat AND I can't reply to it: default the composer
  // to note mode (the only thing this operator can do) when notes are allowed.
  // Reply-all holders keep the reply mode even on a teammate's chat.
  React.useEffect(() => {
    if (readOnlyHere && canNote && mode === "reply") setMode("note");
  }, [readOnlyHere, canNote, mode]);

  const send = () => {
    const value = text.trim();
    if (!value || busy || !canType) return;
    if (isNote) onSendNote(value);
    else onSend(value);
    setText("");
  };

  const headerLabel = isAi
    ? t("helpdesk.composer.aiReplying")
    : otherOwner
      ? t("helpdesk.composer.handledBy", { name: ownerName ?? t("helpdesk.composer.anotherAgent") })
      : isHuman
        ? t("helpdesk.composer.youReplying")
        : isQueued
          ? t("helpdesk.composer.queued")
          : t("helpdesk.composer.resolved");

  const placeholder = isNote
    ? t("helpdesk.composer.placeholderNote")
    : readOnlyHere
      ? t("helpdesk.composer.placeholderOtherOwner")
      : isHuman
        ? t("helpdesk.composer.placeholderReply", { name: custName })
        : isQueued
          ? t("helpdesk.composer.placeholderQueued")
          : t("helpdesk.composer.placeholderAi");

  return (
    <div className="shrink-0 px-4.5 py-3">
      {confirmDialog}
      <div className="w-full">
        <div className="flex items-center justify-between gap-2 rounded-t-lg bg-muted px-3 pb-4.5 pt-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            {isAi ? <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            {headerLabel}
          </span>

          <div className="flex items-center gap-1.5">
            {/* Resolve is available in EVERY open status (ai_handling / queued /
                human_handling) for anyone with handoff:resolve — no claim or
                take-over required. A direct resolve takes no ownership and
                fires no join announcement. */}
            {canResolve && status !== "resolved" ? (
              <Button
                type="button"
                variant="outline"
                data-testid="conv-resolve"
                onClick={() =>
                  confirm({
                    title: t("helpdesk.confirm.resolveTitle"),
                    description: t("helpdesk.confirm.resolveBody"),
                    confirmLabel: t("handoff.resolve"),
                    cancelLabel: t("helpdesk.confirm.cancel"),
                    tone: "default",
                    onConfirm: onResolve,
                  })
                }
                className="h-auto gap-1.5 rounded-md px-2 py-1 text-xs font-medium active:scale-100 [&_svg]:size-3.5"
              >
                <Check className="h-3.5 w-3.5" /> {t("handoff.resolve")}
              </Button>
            ) : null}
            {isAi && canTakeover ? (
              <Button
                type="button"
                variant="ghost"
                data-testid="conv-takeover"
                onClick={() =>
                  confirm({
                    title: t("helpdesk.confirm.takeOverTitle"),
                    description: t("helpdesk.confirm.takeOverBody", { name: custName }),
                    confirmLabel: t("handoff.takeOver"),
                    cancelLabel: t("helpdesk.confirm.cancel"),
                    tone: "default",
                    onConfirm: onTakeover,
                  })
                }
                className="h-auto gap-1.5 rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background transition-opacity hover:bg-foreground hover:text-background hover:opacity-90 active:scale-100 [&_svg]:size-3.5"
              >
                <Headset className="h-3.5 w-3.5" /> {t("handoff.takeOver")}
              </Button>
            ) : isHuman && canHandback ? (
              <Button
                type="button"
                variant="ghost"
                data-testid="conv-handback"
                onClick={() =>
                  confirm({
                    title: t("helpdesk.confirm.handBackTitle"),
                    description: t("helpdesk.confirm.handBackBody"),
                    confirmLabel: t("handoff.handBack"),
                    cancelLabel: t("helpdesk.confirm.cancel"),
                    tone: "default",
                    onConfirm: onHandback,
                  })
                }
                className="h-auto gap-1.5 rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background transition-opacity hover:bg-foreground hover:text-background hover:opacity-90 active:scale-100 [&_svg]:size-3.5"
              >
                <Sparkles className="h-3.5 w-3.5" /> {t("handoff.handBack")}
              </Button>
            ) : isQueued && canClaim ? (
              <Button
                type="button"
                variant="ghost"
                data-testid="conv-claim"
                onClick={onClaim}
                className="h-auto gap-1.5 rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background transition-opacity hover:bg-foreground hover:text-background hover:opacity-90 active:scale-100 [&_svg]:size-3.5"
              >
                <Headset className="h-3.5 w-3.5" /> {t("handoff.claim")}
              </Button>
            ) : null}
          </div>
        </div>

        <div
          className={cn(
            "relative -mt-2.5 rounded-lg border px-3 py-2",
            isNote
              ? "border-[color:color-mix(in_oklch,var(--color-warning)_18%,transparent)] bg-[color:color-mix(in_srgb,var(--color-warning)_5%,var(--card))]"
              : "border-border bg-background",
          )}
        >
          <Textarea
            spellCheck={false}
            disabled={!canType}
            value={text}
            data-testid={isNote ? "conv-note-composer" : "conv-human-composer"}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={placeholder}
            className="-mx-0.5 min-h-[52px] resize-none border-0 bg-transparent px-0.5 py-0 text-2sm shadow-none placeholder:text-2sm placeholder:text-[color:var(--color-fg-subtle)] focus-visible:ring-0"
          />
          <div className="mt-1.5 flex items-center justify-end gap-1.5">
            {canNote ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="conv-mode-toggle"
                    className={cn(
                      "mr-auto h-8 gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground active:scale-100 data-[state=open]:text-foreground [&_svg]:size-3",
                      isNote
                        ? "hover:bg-[color:color-mix(in_srgb,var(--card)_55%,transparent)] data-[state=open]:bg-[color:color-mix(in_srgb,var(--card)_55%,transparent)]"
                        : "hover:bg-muted data-[state=open]:bg-muted",
                    )}
                  >
                    {isNote ? <Note className="h-3 w-3" /> : <SendHorizontal className="h-3 w-3" />}
                    {isNote ? t("helpdesk.composer.modeNote") : t("helpdesk.composer.modeReply")}
                    <ChevronDown className="h-2.5 w-2.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  <DropdownMenuItem onSelect={() => setMode("reply")} className="gap-2 text-2sm">
                    <SendHorizontal className="h-3.5 w-3.5" />
                    {t("helpdesk.composer.modeReply")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setMode("note")} className="gap-2 text-2sm">
                    <Note className="h-3.5 w-3.5" />
                    {t("helpdesk.composer.modeNote")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            <Button
              type="button"
              variant="default"
              size="icon"
              aria-label={isNote ? t("helpdesk.composer.saveNote") : t("helpdesk.composer.send")}
              data-testid={isNote ? "conv-note-send" : "conv-human-send"}
              disabled={!canType || !text.trim() || busy}
              onClick={send}
              className="h-8 w-8 rounded-full bg-foreground text-background transition-opacity hover:opacity-90 active:scale-100 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
